import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { httpsCallable } from 'firebase/functions';
import { clothingService } from '../../services/clothing';
import { Button } from './Button';
import { Input } from './Input';
import { functions } from '../../config/firebase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { cn } from '../../lib/utils';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [rotation, setRotation] = useState(0);
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [color, setColor] = useState('');
  const [loading, setLoading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [error, setError] = useState('');
  const previewUrlRef = useRef<string | null>(null);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clean up preview URL
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setFile(null);
      setPreview(null);
      setRotation(0);
      setCategory('');
      setSubcategory('');
      setColor('');
      setCanEdit(false);
      setCategorizing(false);
      setError('');
    }
  }, [isOpen]);

  const rotateImage = async (imageFile: File, currentRotation: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(imageFile);
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculate new dimensions based on rotation
        const is90or270 = currentRotation === 90 || currentRotation === 270;
        canvas.width = is90or270 ? img.height : img.width;
        canvas.height = is90or270 ? img.width : img.height;

        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((currentRotation * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        canvas.toBlob((blob) => {
          URL.revokeObjectURL(url);
          if (blob) {
            const rotatedFile = new File([blob], imageFile.name, { type: imageFile.type });
            resolve(rotatedFile);
          } else {
            reject(new Error('Failed to create rotated image'));
          }
        }, imageFile.type, 0.95);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  };

  const handleRotate = async () => {
    if (!file) return;
    
    const newRotation = (rotation + 90) % 360;
    
    try {
      // Rotate the actual file - this will be what gets uploaded
      const rotatedFile = await rotateImage(file, newRotation);
      
      // Clean up old preview URL to prevent memory leaks
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      
      // Create new preview URL from rotated file
      const newPreviewUrl = URL.createObjectURL(rotatedFile);
      previewUrlRef.current = newPreviewUrl;
      
      // Update state with rotated file and preview
      setFile(rotatedFile);
      setPreview(newPreviewUrl);
      setRotation(newRotation); // Track rotation for UI purposes
    } catch (err) {
      console.error('Failed to rotate image:', err);
      setError('Failed to rotate image');
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const selectedFile = acceptedFiles[0];
    
    // Clean up old preview URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    
    // Create new preview URL
    const newPreviewUrl = URL.createObjectURL(selectedFile);
    previewUrlRef.current = newPreviewUrl;
    
    setFile(selectedFile);
    setPreview(newPreviewUrl);
    setRotation(0);
    
    // Reset form and disable editing while categorizing
    setCategory('');
    setSubcategory('');
    setColor('');
    setCanEdit(false);
    setCategorizing(true);
    setError('');

    // Automatically categorize with Gemini via Firebase Function
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64Data = (reader.result as string).split(',')[1]; // Remove data:image/...;base64, prefix
          const mimeType = selectedFile.type || 'image/jpeg';

          // Call Firebase Function
          const categorizeImage = httpsCallable(functions, 'categorizeImage');
          const result = await categorizeImage({
            imageData: base64Data,
            mimeType: mimeType,
          });

          const data = result.data as any;

          // Populate fields with Gemini's response
          if (data) {
            setCategory(data.category || '');
            setSubcategory(data.subcategory || '');
            setColor(data.color || '');
          }
        } catch (err: any) {
          console.error('Categorization error:', err);
          // If categorization fails, allow manual entry
          setCategory('shirt'); // Default fallback
        } finally {
          setCategorizing(false);
          setCanEdit(true); // Enable editing after categorization attempt
        }
      };
      reader.onerror = () => {
        console.error('Failed to read file');
        setCategorizing(false);
        setCanEdit(true);
        setCategory('shirt');
      };
      reader.readAsDataURL(selectedFile);
    } catch (err: any) {
      console.error('Categorization error:', err);
      // If categorization fails, allow manual entry
      setCategory('shirt'); // Default fallback
      setCategorizing(false);
      setCanEdit(true);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    maxFiles: 1,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please select an image');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Upload the current file (which may be rotated)
      // The file state always contains the actual file to be uploaded
      await clothingService.create(file, {
        category: category || 'shirt',
        subcategory: subcategory || undefined,
        color: color || undefined,
      });
      
      // Clean up preview URL before closing
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Close modal
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Clothing Item</DialogTitle>
        </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
                isDragActive ? "border-primary bg-primary/10" : "border-input"
              )}
            >
              <input {...getInputProps()} />
              {preview ? (
                <div className="space-y-4">
                  <div className="relative inline-block">
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-64 mx-auto rounded-xl"
                    />
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRotate();
                      }}
                    >
                        Rotate
                    </Button>
                    <p className="text-sm text-gray-600">Click or drag to replace</p>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-lg text-gray-600 mb-2">
                    {isDragActive ? 'Drop the image here' : 'Drag & drop an image here'}
                  </p>
                  <p className="text-sm text-gray-500">or click to select</p>
                </div>
              )}
            </div>

            {categorizing && (
              <div className="bg-primary/10 border-2 border-primary rounded-lg p-4 mb-4">
                <p className="text-center text-foreground font-medium">
                  🔍 Analyzing image with AI...
                </p>
              </div>
            )}

            <Input
              label="Category"
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., shirt, pants, dress"
              disabled={!canEdit}
            />

            <Input
              label="Subcategory (optional)"
              type="text"
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
              placeholder="e.g., t-shirt, jeans"
              disabled={!canEdit}
            />

            <Input
              label="Color (optional)"
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="e.g., blue, red"
              disabled={!canEdit}
            />

            {error && <p className="text-destructive text-sm">{error}</p>}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={loading || !file || categorizing || !canEdit}
              >
                {loading ? 'Uploading...' : categorizing ? 'Analyzing...' : 'Upload'}
              </Button>
            </DialogFooter>
          </form>
      </DialogContent>
    </Dialog>
  );
};

